import Avo from 'analytics/Avo';
import type { Addresses } from 'api/types/addresses.types';
import { useConfig } from 'context/app-config';
import { selectCurrentOrder } from 'pages/newOrder/newOrder.selectors';
import { useGetAllCustomerAddresses } from 'pages/queries/address.queries';
import { usePickupTimeSlots } from 'pages/queries/timeslot.queries';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import useOrderStep from './useOrderStep';

function useAddressSheet({
  toggleLoading,
  isSheetLoadingFeedback,
}: {
  toggleLoading?: (isLoading: boolean) => void;
  isSheetLoadingFeedback?: boolean;
}) {
  const navigate = useNavigate();

  const { addressId } = useSelector(selectCurrentOrder);
  const { incrementStep } = useOrderStep();
  const [showAddressSheet, setShowAddressSheet] = useState(false);
  const toggleAddressSheet = useCallback(() => setShowAddressSheet(!showAddressSheet), [showAddressSheet]);

  const config = useConfig();

  const {
    refetch: refetchAddresses,
    data: addresses,
    fetchStatus: addressesFetchStatus,
  } = useGetAllCustomerAddresses({
    enabled: config?.modules.newOrder.address.showStep,
  });

  // it helps preload data for the next step
  const { refetch: refetchPickup, fetchStatus: pickupFetchStatus } = usePickupTimeSlots({
    addressId,
    enabled: false,
  });

  const onSubmit = useCallback(async () => {
    if (!isSheetLoadingFeedback) {
      setShowAddressSheet(false);
    }

    try {
      toggleLoading?.(true);

      if (config?.modules.newOrder.address.showStep || config?.showSelectServicesOnHomePage) {
        await refetchPickup();
      }
    } finally {
      if (isSheetLoadingFeedback) {
        setShowAddressSheet(false);
      }
      toggleLoading?.(false);
      navigate(`/newOrder/flex`);
    }
  }, [addresses?.length, config?.modules.newOrder.address.showStep, incrementStep, refetchPickup, isSheetLoadingFeedback, toggleLoading, navigate]);

  const continueToAddress = useCallback(async () => {
    Avo.continueSelectService();

    if (!addresses || !addresses?.length) {
      await refetchAddresses();
    }

    // if address is a step then move to add address in next step.
    if (config?.modules.newOrder.address.showStep) {
      if (addresses && addresses?.length > 0) {
        setShowAddressSheet(true);
      } else {
        navigate(`/newOrder/addAddress`);
      }
    } else {
      incrementStep();
    }
  }, [addresses, config?.modules.newOrder.address.showStep, navigate, incrementStep, refetchAddresses]);

  const onClose = useCallback(() => {
    setShowAddressSheet(false);
  }, []);

  const onEdit = useCallback(
    (address?: Addresses.Address) => {
      setShowAddressSheet(false);

      navigate(`/newOrder/editAddress`, {
        state: {
          address,
          goBack: true,
        },
      });
    },
    [navigate]
  );

  const isLoading = useMemo(() => pickupFetchStatus === 'fetching' || addressesFetchStatus === 'fetching', [pickupFetchStatus, addressesFetchStatus]);

  return {
    continueToAddress,
    toggleAddressSheet,
    showAddressSheet,
    onClose,
    isLoading,
    onEdit,
    onSubmit,
  };
}

export default useAddressSheet;
